/**
 * Serves as a Base model for all layer wrappers. Every layer added to the map
 * will be wrapped in one of the child classes that extends this.
 *
 * NOTE: This should not be used directly. Instead one of its child classes
 * should be used.
 */

var _ = require('lodash');

var EventMixin = require('./mixins/EventMixin');
var PopupMixin = require('./mixins/PopupMixin');
var StoreMixin = require('./mixins/StoreMixin');
var StyleMixin = require('./mixins/StyleMixin');
var LabelMixin = require('./mixins/LabelMixin');
var InfoBoxMixin = require('./mixins/InfoBoxMixin');

function BaseLayer (layer, leafletLayer, collection, options, Actions, Store) {

  _.extend(this, {
    id: layer.$$layerUID,
    collection: collection,
    Actions: Actions,
    dialogPopup: null, // available when the layer is active
    isActive: false, // true if currently the active layer (i.e. being edited)
    isPopupOpen: false,
    layer: layer, // data layer
    leafletLayer: leafletLayer, // leaflet layer
    map: null, // not set (or needed) until after the layer has been rendered
    options: options,
    styles: options.styles,
    Store: Store,
    _ref: options._ref // Internal use, maps layer with the resource that created it
  });

  if (layer.type !== 'overlay') {
    this._registerEventListeners();
    this._addStoreListeners();

    // set to default style
    this.setStyleForState('default');
    this.setLabels();
    this.setLabelEvents();
    this.setInfoBox();
    this.setInfoBoxEvents();
  }

  this.initialize.apply(this, arguments);
}

BaseLayer.create = function () {
  var inst = Object.create(this.prototype);
  var obj = this.apply(inst, arguments);
  return Object(obj) === obj ? obj : inst;
};

/**
 * Extend method to be used by child classes
 * @param  {Object|Array} protoProps Object or Array of Object to extend prototype with
 * @param  {Object} staticProps
 */
BaseLayer.extend = function (protoProps, staticProps) {
  var parent = this;
  var child;

  // Setup the child constructor
  if (protoProps && _.has(protoProps, 'constructor')) {
    child = protoProps.constructor;
  } else {
    child = function () {
      return parent.apply(this, arguments);
    };
  }

  _.extend(child, parent, staticProps);

  var Surrogate = function () {
    this.constructor = child;
  };

  Surrogate.prototype = parent.prototype;
  child.prototype = new Surrogate();

  if (protoProps) {
    if (_.isArray(protoProps)) {
      _.extend.apply(null, [child.prototype].concat(protoProps));
    } else {
      _.extend(child.prototype, protoProps);
    }
  }

  child.__super__ = parent.prototype;
  return child;
};

BaseLayer.prototype = {

  initialize: function () {
    // noop
  },

  isClickable: function () {
    var collection = this.collection;
    var editableTypes = this.Store.get('editableTypes');

    if (editableTypes !== null && typeof editableTypes !== 'undefined') {
      // editableTypes is used for runtime toggling of clickable
      // ... BUT the settings at creation time ALWAYS take precendence so you
      // ... if what rendered as uneditable/unclickable then adding it to
      // ... editableTypes will not change that.
      return editableTypes.indexOf(collection) !== -1
        && (this.options.isClickable || this.options.isEditable);
    }
  },

  isEditable: function () {
    var collection = this.collection;
    var editableTypes = this.Store.get('editableTypes');

    if (editableTypes !== null && typeof editableTypes !== 'undefined') {
      // editableTypes is used for runtime toggling of clickable
      // ... BUT the settings at creation time ALWAYS take precendence so you
      // ... if what rendered as uneditable/unclickable then adding it to
      // ... editableTypes will not change that.
      return editableTypes.indexOf(collection) !== -1
        && this.options.isEditable;
    }
  },

  toggleEditable: function () {
    if (this.isClickable()) {
      // Not all elements (markers, text) have _container, so check
      if (this.leafletLayer._container) {
        this.leafletLayer._container.style.pointerEvents = 'auto';
      }
    } else {
      // let events go through to the map or other layers if not clickable...
      // Not all elements (markers, text) have _container, so check
      if (this.leafletLayer._container) {
        this.leafletLayer._container.style.pointerEvents = 'none';
      }
    }
  },

  disableEditing: function () {
    var leafletLayer = this.leafletLayer;

    if (leafletLayer.editing &&
        leafletLayer.editing._enabled) {
      leafletLayer.editing.disable();
    }
  },

  enableEditing: function () {
    var leafletLayer = this.leafletLayer;

    if (this.isEditable() &&
        !leafletLayer.editing._enabled) {
      leafletLayer.editing.enable();
    }
  },

  setActive: function () {
    if (!this.isActive) {
      this.setStyleForState('active');
      this.isActive = true;
      this.initializePopup();
    }
  },

  setInactive: function () {
    if (this.isActive) {
      this.setPopupLatLng(null);
      this.setStyleForState('default');
      this.cleanupPopup();
      this.isActive = false;
      this.Actions.UnsetActiveLayer.dispatch(this.layer);
    }
  },
};

// extend prototype with the mixins
_.extend(BaseLayer.prototype, EventMixin, PopupMixin, StoreMixin, StyleMixin, LabelMixin, InfoBoxMixin);

module.exports = BaseLayer;



// WEBPACK FOOTER //
// ./~/@skycatch/skydash-saturn/boosters/map/src/app/layer/edit/base/BaseLayer.js